import { useContext } from "react";
import { W, E, NW, NE, SW, SE } from "../constants/editor";
import {
  getZoomedValue,
  getUnScaledValue,
  calculateNewPositionOnRotatedObjectResize,
  getCssTransformObj,
} from "../_helpers/utils";
import { EditorContext } from "../containers/editor/EditorLayout";
import UseCheckWidgetAllignment from "./UseCheckWidgetAllignment";
import useCheckWidgetPosition from "./useCheckWidgetPosition";
import useDeleteWidget from "./useDeleteWidget";

const useResizeTextArtWidget = () => {
  let { widgets, updateWidgets, dimension } = useContext(EditorContext);

  const { checkWidgetAllignmentForSingleWidget } = UseCheckWidgetAllignment();
  const deleteWidgetIfOutside = useCheckWidgetPosition();
  const setDeleteWidget = useDeleteWidget();

  const textArtResizeStart = data => {
    let noOfLines =
      getUnScaledValue(data.client.initial.height, dimension.zoom) /
      parseFloat(document.querySelector(`#${data.client.id} .text-content-editor`).style.fontSize);

    data.client.initial.noOfLines = noOfLines;
  };

  const textArtResize = (meta, widgetHeight, widgetWidth) => {
    let mainObj = document.getElementById(meta.client.id);
    let handler = document.getElementById("dhp-widget-handler");

    if (
      mainObj.querySelector(".text-content-editor").getAttribute("data-transform-type") === "Path 1" ||
      !mainObj.querySelector(".text-content-editor").getAttribute("data-transform-type")
    ) {
      const inner = mainObj.querySelector(".dhp-widget-inner");
      const textEl = inner.querySelector(".text-content-editor");

      // 🔥 CRITICAL: vertical centering
      inner.style.display = "flex";
      inner.style.alignItems = "center";

      let fontSize = widgetHeight / meta.client.initial.noOfLines;
      fontSize = fontSize < 100 ? +fontSize.toFixed(1) : Math.round(fontSize);

      textEl.style.whiteSpace = "nowrap";
      textEl.style.fontSize = `${fontSize}px`;
      textEl.style.lineHeight = `${fontSize}px`;

      // width fit (already working)
      const targetWidth = inner.clientWidth;
      const textWidth = textEl.scrollWidth;
      if (textWidth > 0) {
        const scale = targetWidth / textWidth;
        fontSize *= scale;
        textEl.style.fontSize = `${fontSize}px`;
        textEl.style.lineHeight = `${fontSize}px`;
      }

      const resizedHeight = inner.offsetHeight;
      mainObj.style.height = `${resizedHeight}px`;
      handler.style.height = `${getZoomedValue(resizedHeight, dimension.zoom)}px`;

      meta.client.current = {
        ...meta.client.current,
        height: `${resizedHeight}px`,
      };
    } else {
      let fontSize = widgetHeight / meta.client.initial.noOfLines;
      fontSize = fontSize < 100 ? fontSize.toFixed(1) : Math.round(fontSize);

      // Update text inner widget width and font size
      mainObj.querySelector(".text-content-editor").style.fontSize = `${fontSize}px`;
      mainObj.querySelector(".text-content-editor").style.whiteSpace = "nowrap";
      mainObj.querySelector(".text-content-editor").style.height = "auto"; // or fixed height

      // Update text main div height and handler height
      let resizedHeight = mainObj.querySelector(".dhp-widget-inner").offsetHeight;
      mainObj.style.height = `${resizedHeight}px`;
      handler.style.height = `${getZoomedValue(resizedHeight, dimension.zoom)}px`;

      // update context obj
      meta.client.current = {
        ...meta.client.current,
        height: `${resizedHeight}px`,
      };
    }
  };

  const textArtResizestop = meta => {
    const mainObj = document.getElementById(meta.client.id);
    const updateInnerHtml = mainObj.innerHTML;
    let updatedCss = meta.client.current;
    const { x_al, y_al } = checkWidgetAllignmentForSingleWidget(
      updatedCss.height,
      updatedCss.transform,
      updatedCss.width
    );
    const isDletable = deleteWidgetIfOutside(meta.client.id);

    // Check widget position during resize, if it is outside canvas area delete the widget else upadte new position
    if (isDletable) setDeleteWidget(true);
    else {
      let handler = document.getElementById("dhp-widget-handler");
      let resizedHeight = mainObj.querySelector(".dhp-widget-inner").offsetHeight;
      // adjust left top for rotated Text resize
      const {
        translate: { x: widgetTransX, y: widgetTransY },
        rotate: { theta: widgetTheta },
      } = getCssTransformObj({
        transform: mainObj.style.transform,
      });
      const { left, top } = calculateNewPositionOnRotatedObjectResize(
        parseFloat(widgetTransX),
        parseFloat(widgetTransY),
        parseFloat(updatedCss.width),
        parseFloat(resizedHeight),
        parseFloat(mainObj.style.width),
        parseFloat(mainObj.style.height),
        parseFloat(widgetTheta)
      );
      const widgetTransformStr = getCssTransformObj({
        translateX: `${left}px`,
        translateY: `${top}px`,
        rotateAngle: widgetTheta,
        returnStr: true,
      });

      // Update text main div transform and height
      mainObj.style.transform = widgetTransformStr;
      mainObj.style.height = `${resizedHeight}px`;

      // set handler transform and height
      const handlerTransformStr = getCssTransformObj({
        translateX: `${getZoomedValue(left, dimension.zoom)}px`,
        translateY: `${getZoomedValue(top, dimension.zoom)}px`,
        rotateAngle: widgetTheta,
        returnStr: true,
      });
      handler.style.transform = handlerTransformStr;
      handler.style.height = `${getZoomedValue(resizedHeight, dimension.zoom)}px`;

      updatedCss = { ...updatedCss, height: `${resizedHeight}px`, transform: widgetTransformStr };
      updateWidgets(
        widgets.map((widget, idx) => {
          if (idx === meta.client.idx) {
            return {
              ...widget,
              style: {
                ...widget.style,
                ...updatedCss,
              },
              data: {
                ...widget.data,
                "data-x-allignment": x_al,
                "data-y-allignment": y_al,
              },
              innerHTML: updateInnerHtml,
            };
          } else {
            return widget;
          }
        })
      );
    }
  };

  return [textArtResizeStart, textArtResize, textArtResizestop];
};

export default useResizeTextArtWidget;
